import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { FaMapLocationDot } from "react-icons/fa6";
import { toast } from "react-toastify";
import {
  fetchRegions,
  deleteRegions,
  clearState,
} from "../../redux/Slices/RegionsSlice";
import { useTitle } from "../../context/TitleContext";
import PermissionButton from "../PermissionButton";
import Pagination from '../../components/Pagination';

const Regions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRegion, setDeletingRegion] = useState(null);

  const { regions, meta, isLoading, error, success } = useSelector(
    (state) => state.regions
  );

  useEffect(() => {
    setTitle("المناطق");
  }, [setTitle]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchRegions(currentPage));
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (success) {
      setShowDeleteModal(false);
      setDeletingRegion(null);
      setTimeout(() => {
        toast.success("تم الحذف بنجاح", { position: "top-center", zIndex: 9999 });
      }, 100);
      dispatch(fetchRegions(currentPage));
      dispatch(clearState());
    }
    if (error) {
      toast.error("لم يتم الحذف", { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }
  }, [success, error, dispatch, currentPage]);

  const handlePageChange = (page) => {
    if (isLoading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleDelete = (region) => {
    setDeletingRegion(region);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingRegion) {
      dispatch(deleteRegions(deletingRegion.id));
      setShowDeleteModal(false);
      setDeletingRegion(null);
    }
  };



  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (isLoading && (!regions || !regions.data || regions.data.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .region-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .region-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .region-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            🗺 المناطق والأحياء (Regions)
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل ومراقبة وإضافة مناطق الشحن وعناوين الطلاب التابعة لكل مدينة</p>
        </div>
        <PermissionButton
          permission="create-regions"
          onClick={() => navigate("/add-region")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          <FaPlus size={12} /> إضافة منطقة جديدة
        </PermissionButton>
      </div>

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!regions || !regions.data || regions.data.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <FaMapLocationDot size={48} className="text-muted mb-3" />
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد مناطق مسجلة حالياً</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="region-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>اسم المنطقة (عربي)</th>
                    <th>اسم المنطقة (EN)</th>
                    <th>المدينة التابعة لها (عربي)</th>
                    <th>المدينة التابعة لها (EN)</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.data.map((region, index) => (
                    <tr key={region.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>{region.name_ar}</td>
                      <td style={{ color: "#475569" }}>{region.name_en}</td>
                      <td>{region.city_name_ar}</td>
                      <td>{region.city_name_en}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: region.status === "active" ? "#15803d" : "#64748b", background: region.status === "active" ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {region.status === "active" ? "مفعل" : "غير مفعل"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => {
                              sessionStorage.setItem(`region_${region.id}`, JSON.stringify({
                                id: region.id,
                                name_ar: region.name_ar,
                                name_en: region.name_en,
                              }));
                              navigate(`/edit-region/${region.id}`);
                            }}
                            permission="edit-regions"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(region)}
                            permission="delete-regions"
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
               <Pagination meta={meta} currentPage={currentPage} loading={isLoading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingRegion(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذه المنطقة؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                🗺 {deletingRegion?.name_ar} ({deletingRegion?.name_en})
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه وسيحذف كافة عناوين الشحن المرتبطة به.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingRegion(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer" }}>حذف المنطقة</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Regions;
